<template>
  <div class="gla-auth-login gla-auth-ui-surface" :data-layout="layout">
    <!-- Hero: gradient full screen + optional logo slot (CallTrack main app) -->
    <div v-if="layout === 'hero'" class="gla-auth-login__hero">
      <div class="gla-auth-login__hero-inner">
        <div v-if="$slots.logo" class="gla-auth-login__logo-slot">
          <slot name="logo" />
        </div>
        <div v-if="title || subtitle" class="gla-auth-login__intro">
          <h1 v-if="title" class="gla-auth-login__title">{{ title }}</h1>
          <p v-if="subtitle" class="gla-auth-login__subtitle">{{ subtitle }}</p>
        </div>
        <div class="gla-auth-login__buttons">
          <button
            v-if="showGoogle"
            type="button"
            class="gla-auth-login__btn gla-auth-login__btn--google"
            @click="onGoogle"
          >
            <span class="gla-auth-login__btn-icon" aria-hidden="true">
              <svg class="gla-auth-login__svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            <span>{{ googleLabel }}</span>
          </button>
          <button
            v-if="showMicrosoft"
            type="button"
            class="gla-auth-login__btn gla-auth-login__btn--microsoft"
            @click="onMicrosoft"
          >
            <span class="gla-auth-login__btn-icon" aria-hidden="true">
              <svg class="gla-auth-login__svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#F25022" />
                <path d="M24 11.4H12.6V0H24v11.4z" fill="#7FBA00" />
                <path d="M11.4 24H0V12.6h11.4V24z" fill="#00A4EF" />
                <path d="M24 24H12.6V12.6H24V24z" fill="#FFB900" />
              </svg>
            </span>
            <span>{{ microsoftLabel }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Card: centered panel (Professor, optional) -->
    <div v-else-if="layout === 'card'" class="gla-auth-login__screen gla-auth-login__screen--card">
      <div class="gla-auth-login__card">
        <div v-if="title || subtitle" class="gla-auth-login__intro">
          <h1 v-if="title" class="gla-auth-login__title">{{ title }}</h1>
          <p v-if="subtitle" class="gla-auth-login__subtitle">{{ subtitle }}</p>
        </div>
        <div class="gla-auth-login__buttons">
          <button
            v-if="showGoogle"
            type="button"
            class="gla-auth-login__btn gla-auth-login__btn--google"
            @click="onGoogle"
          >
            <span class="gla-auth-login__btn-icon" aria-hidden="true">
              <svg class="gla-auth-login__svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            <span>{{ googleLabel }}</span>
          </button>
          <button
            v-if="showMicrosoft"
            type="button"
            class="gla-auth-login__btn gla-auth-login__btn--microsoft"
            @click="onMicrosoft"
          >
            <span class="gla-auth-login__btn-icon" aria-hidden="true">
              <svg class="gla-auth-login__svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#F25022" />
                <path d="M24 11.4H12.6V0H24v11.4z" fill="#7FBA00" />
                <path d="M11.4 24H0V12.6h11.4V24z" fill="#00A4EF" />
                <path d="M24 24H12.6V12.6H24V24z" fill="#FFB900" />
              </svg>
            </span>
            <span>{{ microsoftLabel }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Stack: minimal centered (admin) -->
    <div v-else class="gla-auth-login__stack">
      <div v-if="title || subtitle" class="gla-auth-login__intro">
        <h1 v-if="title" class="gla-auth-login__title">{{ title }}</h1>
        <p v-if="subtitle" class="gla-auth-login__subtitle">{{ subtitle }}</p>
      </div>
      <div class="gla-auth-login__buttons">
        <button
          v-if="showGoogle"
          type="button"
          class="gla-auth-login__btn gla-auth-login__btn--google"
          @click="onGoogle"
        >
          <span class="gla-auth-login__btn-icon" aria-hidden="true">
            <svg class="gla-auth-login__svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          </span>
          <span>{{ googleLabel }}</span>
        </button>
        <button
          v-if="showMicrosoft"
          type="button"
          class="gla-auth-login__btn gla-auth-login__btn--microsoft"
          @click="onMicrosoft"
        >
          <span class="gla-auth-login__btn-icon" aria-hidden="true">
            <svg class="gla-auth-login__svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#F25022" />
              <path d="M24 11.4H12.6V0H24v11.4z" fill="#7FBA00" />
              <path d="M11.4 24H0V12.6h11.4V24z" fill="#00A4EF" />
              <path d="M24 24H12.6V12.6H24V24z" fill="#FFB900" />
            </svg>
          </span>
          <span>{{ microsoftLabel }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Auth } from 'firebase/auth';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

const props = withDefaults(
  defineProps<{
    auth: Auth;
    showGoogle?: boolean;
    showMicrosoft?: boolean;
    googleLabel: string;
    microsoftLabel: string;
    title?: string;
    subtitle?: string;
    /** hero = gradient + logo slot; card = panel; stack = minimal centered */
    layout?: 'hero' | 'card' | 'stack';
    /** Passed to OAuthProvider.setCustomParameters when signing in with Microsoft */
    microsoftCustomParameters?: Record<string, string>;
  }>(),
  {
    showGoogle: true,
    showMicrosoft: false,
    layout: 'stack',
  },
);

function ignoreUserCancelled(e: unknown) {
  if (e && typeof e === 'object' && 'code' in e) {
    const code = (e as { code: string }).code;
    if (code === 'auth/cancelled-popup-request' || code === 'auth/popup-closed-by-user') {
      return;
    }
  }
  console.error('[GlanceAuthLogin] Sign-in error:', e);
}

function onGoogle() {
  signInWithPopup(props.auth, new GoogleAuthProvider()).catch(ignoreUserCancelled);
}

function onMicrosoft() {
  const provider = new OAuthProvider('microsoft.com');
  if (props.microsoftCustomParameters && Object.keys(props.microsoftCustomParameters).length > 0) {
    provider.setCustomParameters(props.microsoftCustomParameters);
  }
  signInWithPopup(props.auth, provider).catch(ignoreUserCancelled);
}
</script>

<style scoped>
.gla-auth-login {
  box-sizing: border-box;
  width: 100vw;
  min-width: 100vw;
  max-width: 100vw;
  height: 100vh;
  min-height: 100vh;
  margin: 0;
  overflow: auto;
}

.gla-auth-login__hero {
  display: flex;
  height: 100%;
  min-height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: linear-gradient(345deg, rgba(9, 26, 74, 1) 20%, rgba(10, 79, 152, 1) 100%);
  padding: 24px 24px 20vh;
  box-sizing: border-box;
}

.gla-auth-login__hero-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 480px;
}

.gla-auth-login__logo-slot {
  margin-bottom: 40px;
}

.gla-auth-login__stack {
  display: flex;
  height: 100%;
  min-height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  box-sizing: border-box;
}

.gla-auth-login__screen--card {
  height: 100%;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--gla-auth-screen-bg, #f4f6f9);
  box-sizing: border-box;
}

.gla-auth-login__screen {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 100%;
}

.gla-auth-login__card {
  width: 100%;
  max-width: 400px;
  background: var(--gla-auth-card-bg, #fff);
  border: 1px solid var(--gla-auth-card-border, #e5e7eb);
  border-radius: 12px;
  padding: 32px 28px;
  box-shadow: var(--gla-auth-card-shadow, 0 4px 24px rgba(0, 0, 0, 0.06));
  box-sizing: border-box;
}

.gla-auth-login__intro {
  margin-bottom: 20px;
  text-align: center;
}

.gla-auth-login__title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  color: var(--gla-auth-title, #0f172a);
}

.gla-auth-login__subtitle {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: var(--gla-auth-subtitle, #64748b);
}

.gla-auth-login__buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.gla-auth-login__btn {
  padding: 12px 24px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  border-radius: 8px;
  min-width: 280px;
  font-size: 15px;
  font-weight: 500;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
}

.gla-auth-login__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.gla-auth-login__btn:active {
  transform: translateY(0);
}

.gla-auth-login__btn-icon {
  display: flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.gla-auth-login__svg {
  width: 20px;
  height: 20px;
  display: block;
}

.gla-auth-login__btn--google {
  background: #ffffff;
  color: #3c4043;
  border: 1px solid #dadce0;
}

.gla-auth-login__btn--google:hover {
  background: #f8f9fa;
  border-color: #d2d3d5;
}

.gla-auth-login__btn--microsoft {
  background: #2f2f2f;
  color: #ffffff;
}

.gla-auth-login__btn--microsoft:hover {
  background: #3d3d3d;
}

.gla-auth-login[data-layout='hero'] .gla-auth-login__btn--google {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.gla-auth-login[data-layout='hero'] .gla-auth-login__btn--google:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
</style>
